import type { ReceiptPrintData } from '../types';
import { ReceiptQrCode } from './ReceiptQrCode';
import { PAYMENT_METHOD_LABELS, formatMoney, formatShortDate, formatPlainDate } from '../utils/receiptFormat';
import { getAffiliation, AFFILIATION_CATEGORY_CODE } from '../utils/affiliation';

interface Props {
  data: ReceiptPrintData;
}

/** ~80mm thermal roll layout, matching RECIBO ROLLO.pdf. */
export function ThermalReceiptPrint({ data }: Props) {
  const numberStr = String(data.number).padStart(6, '0');
  const isVoid = data.status === 'void';
  const cur = data.currency === 'USD' ? 'USD' : 'Bs';
  const affiliation = getAffiliation(data);

  return (
    <div
      className="mx-auto bg-white text-black"
      style={{ width: '80mm', padding: '4mm', fontFamily: 'sans-serif', fontSize: '10px', lineHeight: 1.35 }}
    >
      <div className="text-center">
        <p className="text-[11px] font-bold">LAS ASAMBLEAS DE DIOS DE BOLIVIA</p>
        <p className="text-[8px]">CONSTRUYENDO EL FUTURO, HONRANDO LA HISTORIA</p>
        <p className="text-[8px]">R.S. 101452/61 - 215651/95</p>
        <p className="mt-1 text-[8px]">Oficina Central – Calle Ballivián esq. Bueno #1497</p>
        <p className="text-[8px]">Oficina Sopocachi – Av. Ecuador esq. Pedro Salazar #2599</p>
        <p className="text-[8px]">La Paz - Bolivia</p>
        <p className="text-[8px]">Tel. 73510370 – 2200667</p>
      </div>

      <div className="my-2 border-t border-dashed border-black" />

      <div className="text-center">
        <p className="text-[11px] font-bold">RECIBO OFICIAL</p>
        {data.categoryCode === AFFILIATION_CATEGORY_CODE && (
          <p className="text-[10px] font-bold uppercase">{data.categoryName}</p>
        )}
        {isVoid && <p className="text-[11px] font-bold">*** ANULADO ***</p>}
        <p className="text-[9px]">RECIBO N°</p>
        <p className="text-[13px] font-bold">{numberStr}</p>
      </div>

      <div className="my-2 border-t border-dashed border-black" />

      <p><strong>NOMBRE:</strong> {data.payerName}</p>
      {data.payerCi && <p><strong>C.I.:</strong> {data.payerCi}</p>}
      <p><strong>FECHA:</strong> {formatShortDate(data.issuedAt)}</p>

      {affiliation && (
        <>
          {affiliation.affiliation_date && <p><strong>F. AFILIACIÓN:</strong> {formatPlainDate(affiliation.affiliation_date)}</p>}
          {affiliation.adb_code && <p><strong>CÓDIGO ADB:</strong> {affiliation.adb_code}</p>}
          {affiliation.district && <p><strong>DISTRITO:</strong> {affiliation.district}</p>}
          {affiliation.region && <p><strong>REGIÓN:</strong> {affiliation.region}</p>}
        </>
      )}

      <div className="my-2 border-t border-dashed border-black" />

      <p className="text-center font-bold">DETALLE</p>
      {data.lines.map((line) => (
        <div key={line.lineNo} className="mt-1">
          <p>{line.description}</p>
          <div className="flex justify-between">
            <span>{line.quantity.toFixed(2)} X {formatMoney(line.unitPrice)}</span>
            <span>{formatMoney(line.subtotal)}</span>
          </div>
        </div>
      ))}

      <div className="my-2 border-t border-dashed border-black" />

      <Row label={`SUBTOTAL ${cur}`} value={formatMoney(data.subtotal)} />
      <Row label={`DESCUENTO ${cur}`} value={formatMoney(data.discount)} />
      {data.payments.map((p, i) => (
        <Row key={i} label={`${PAYMENT_METHOD_LABELS[p.method].toUpperCase()} ${cur}`} value={formatMoney(p.amount)} />
      ))}
      <div className="my-1 border-t border-black" />
      <Row label={`TOTAL ${cur}`} value={formatMoney(data.total)} bold />
      {data.currency === 'USD' && data.exchangeRate && (
        <p className="text-right text-[8px]">T/C: {data.exchangeRate}</p>
      )}

      <div className="my-2 border-t border-dashed border-black" />

      <p>Son: {data.totalInWords}</p>

      <div className="my-2 border-t border-dashed border-black" />

      <p className="text-center text-[8px] italic">
        Pagad a todos lo que debéis: al que tributo, tributo; al que impuesto, impuesto; al que
        respeto, respeto; al que honra, honra.
      </p>
      <p className="text-center text-[8px] font-bold">Romanos 13:7</p>

      <p className="mt-2 text-center text-[7px]">
        Este documento es el resumen gráfico del documento emitido por el sistema integral de Las
        Asambleas de Dios de Bolivia y puede ser descargado escaneando el siguiente código QR.
      </p>

      <div className="mt-2 flex justify-center">
        <ReceiptQrCode url={data.publicUrl} size={90} />
      </div>

      {isVoid && <p className="mt-2 text-center text-[11px] font-bold">*** ANULADO ***</p>}
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between ${bold ? 'font-bold' : ''}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
